import { useEffect, useRef, useState } from 'react';
import AlphabetListWithLetters from '@apps/components/AlphabetList';
import { BrandFilter } from '@apps/components/BrandFilter';
import { DialogRefProps } from '@apps/components/Dialog';
import NewCarBrandGroup from '@apps/components/NewCarBrand';
import Popover from '@apps/components/Popover';
import { QuickActionButtonList } from '@apps/components/QuickActionButtonList';
import QuickFilterButton from '@apps/components/QuickFilterButton';
import { SectionTitle } from '@apps/components/SectionTitle';
import {
  getCarByBrand,
  getNewCarByFilter,
} from '@apps/config/API/new-car.config';
import CarDescriptionSection from '@apps/containers/CarDescription';
import CarListingSection from '@apps/containers/CarListing';
import FilterSection from '@apps/containers/FilterSection';
import RecommendCarList from '@apps/containers/RecommendCarList';
import {
  getQuickActionButtons,
  quickFilters,
  useQuickButtonGroup,
} from '@apps/screens/NewCar/settings';
import {
  generateQueryToUrl,
  generateUrlToQuery,
} from '@apps/utils/query-to-url';
import ClearIcon from '@mui/icons-material/Clear';
import { Avatar, Button, Grid, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FilterIcon from '@stack/assets/icons/filter.svg';
import SearchIcon from '@stack/assets/icons/search-primary.svg';
import createBasePath from '@stack/helpers/lib/createBasePath';
import { useGetResponsive } from '@stack/helpers/lib/responsive';
import { TitleDivider } from '@stack/ui/components';
import Breadcrumb from '@stack/ui/components/Breadcrumb';
import { BreadCrumbOptions } from '@stack/ui/components/Breadcrumb/props';
import { redirect } from '@webbyx/next-js';
import _, { size } from 'lodash';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { useRouter } from 'next/router';

/**
 * ===========================
 * MAIN
 * ===========================
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export type SegmentType = {
  car_segment_type_name: string;
  car_segment_type_slug: string;
};
export type CapacityType = {
  capacity_name: string;
  capacity_slug: string;
};
export type FuelType = {
  car_fuel_type_id: number;
  car_fuel_type_name: string;
  car_fuel_type_slug: string;
};
export type TransmissionType = { transmission: string; total: number };
export type SeatType = { seat_name: number; seat_slug: number };

export const NewCarScreen = (props: {
  carBrand: any;
  newCarEV: any;
  popularCar: any;
  popularBrand: any;
  newCarLaunch?: any;
  newCarModels?: any;
  filterList?: any;
  settings?: {
    budget: number[];
    monthly_payment: number[];
    segment: SegmentType[];
    capacity: CapacityType[];
    fuel_type: FuelType[];
    transmission: TransmissionType[];
    seat: SeatType[];
  };
  webView?: boolean;
}) => {
  // const { t } = useTranslation('common');
  // =============== VARIABLES
  const {
    carBrand,
    newCarLaunch,
    newCarModels,
    newCarEV,
    // popularCar,
    popularBrand,
    filterList,
    settings,
    webView,
  } = props;

  const { basePath } = createBasePath({ webView, type: 'cars' });

  // =============== VARIABLES
  const desktopPopularBrands = popularBrand?.slice(0, 12) ?? [];
  const mobilePopularBrands = popularBrand?.slice(0, 14) ?? [];
  const tabletPopularBrands = popularBrand?.slice(0, 17) ?? [];

  // =============== HOOKS
  const router = useRouter();
  const { isMobile } = useGetResponsive();
  const [data, setData] = useState({
    newCarLaunch,
    newCarModels,
    newCarEV,
  });
  const query = router?.query;
  const budget = settings?.budget?.slice(0, -1);
  const monthlyPayment = settings?.monthly_payment?.slice(0, -1);
  const minBudget = Math.min(...(budget ?? []));
  const maxBudget = Math.max(...(budget ?? []));
  const minMonthly = Math.min(...(monthlyPayment ?? []));
  const maxMonthly = Math.max(...(monthlyPayment ?? []));
  const defaultBudget =
    query['min_budget'] || query['max_budget']
      ? {
          budget: [
            query['min_budget'] ?? minBudget,
            query['max_budget'] ?? maxBudget,
          ],
        }
      : {};
  const defaultMonthly =
    query['min_monthly_payment'] || query['max_monthly_payment']
      ? {
          monthly_payment: [
            query['min_monthly_payment'] ?? minBudget,
            query['max_monthly_payment'] ?? maxBudget,
          ],
        }
      : {};
  const defaultFilter = query
    ? generateUrlToQuery(query as any, [
        'min_budget',
        'max_budget',
        'min_monthly_payment',
        'max_monthly_payment',
        'page',
      ])
    : null;
  const filterPagination = filterList?.meta;
  const [filterData, setFilterData] = useState(filterList?.data);
  const formatter = new Intl.NumberFormat('en-US');
  const [popperItem, setPopperItem] = useState<typeof quickButtons[0] | null>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterValue, setFilterValue] = useState<any>({
    ...defaultFilter,
    ...defaultBudget,
    ...defaultMonthly,
  });
  const [keyword, setKeyword] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const filterDialogRef = useRef<DialogRefProps>(null);

  const quickButtons = useQuickButtonGroup({
    budgetList: budget,
    monthlyPaymentList: monthlyPayment,
  });

  useEffect(() => {
    const { page } = router.query;
    if (page) {
      onFilter({ ...defaultFilter, ...defaultBudget, ...defaultMonthly, page });
    }
  }, [router?.asPath, router?.locale]);

  // =============== EVENTS
  const onHandleClose = () => {
    setPopperItem(null);
    setAnchorEl(null);
  };

  const onHandleClick = (
    selectedItem: typeof quickButtons[0],
    event: React.MouseEvent<HTMLElement>
  ) => {
    if (!selectedItem?.render) {
      onFilter(selectedItem?.filterQuery);
      return;
    }
    setAnchorEl(event?.currentTarget);
    if (popperItem?.key === selectedItem?.key) {
      onHandleClose();
      return;
    }
    setPopperItem(selectedItem);
  };

  const onReset = () => {
    router.replace(
      {
        query: {},
      },
      undefined,
      { shallow: true }
    );
    setFilterValue(null);
    setFilterData(undefined);
    setDialogOpen(false);
  };

  const onFilter = async (query: any) => {
    setFilterValue(query);
    const pathQuery = checkDefault(query) as any;
    let newCarData = {} as any;
    const filterUrl = generateQueryToUrl(pathQuery, basePath);
    redirect({}, filterUrl, 'replace');
    if (!_.isEmpty(pathQuery)) {
      newCarData = await getNewCarByFilter?.({ query: pathQuery });
    }
    setFilterData(newCarData?.data);
    setDialogOpen(false);
  };

  const onFilterBrand = async ({
    slug,
    params,
    routingMethod = 'push',
  }: {
    slug?: string;
    params?: Record<string, string>;
    routingMethod?: 'push' | 'replace';
  }) => {
    let path = [basePath, slug].filter(Boolean).join('/');
    if (params) {
      const queryParams = new URLSearchParams(params);
      path += `?${queryParams.toString()}`;
    }
    router[routingMethod || 'push'](path, undefined, {
      shallow: false,
    });
    const newBrandCar = await getCarByBrand({ brand: slug, params });
    setData((prevState) => ({
      ...prevState,
      newCarModels: newBrandCar?.data?.all_car_model?.data,
      newCarEV: newBrandCar?.ev_car_models,
    }));
  };

  const filterRangeInput = (
    minKey: string,
    maxKey: string,
    minValue: number,
    maxValue: number,
    value?: string | string[]
  ) => {
    if (!value) return {};
    const [min, max] = value;
    let routerQuery: {
      [key: string]: string | undefined | string[];
    } = {
      [minKey]: min,
      [maxKey]: max,
    };
    if (Number(min) === minValue) {
      routerQuery = omit(routerQuery, [minKey]);
    }
    if (Number(max) === maxValue) {
      routerQuery = omit(routerQuery, [maxKey]);
    }
    return routerQuery;
  };

  const checkDefault = (value: any) => {
    const segment_type = value?.['segment'];
    const body_type = value?.['body_type'];
    const budget = value?.['budget'];
    const monthlyPayment = value?.['monthly_payment'];
    const budgetValue = filterRangeInput(
      'min_budget',
      'max_budget',
      minBudget,
      maxBudget,
      budget
    );
    const monthlyPaymentValue = filterRangeInput(
      'min_monthly_payment',
      'max_monthly_payment',
      minMonthly,
      maxMonthly,
      monthlyPayment
    );

    return {
      ...(budget && budgetValue),
      ...(monthlyPayment && monthlyPaymentValue),
      ...(value?.['min_budget'] && { min_budget: value['min_budget'] }),
      ...(value?.['max_budget'] && { min_budget: value['max_budget'] }),
      ...(value?.['min_monthly_payment'] && {
        min_budget: value['min_monthly_payment'],
      }),
      ...(value?.['max_monthly_payment'] && {
        min_budget: value['max_monthly_payment'],
      }),
      ...(value?.['body_type'] &&
        !isEmpty(value['body_type']) && { body_type: body_type }),
      ...(value?.['segment'] &&
        !isEmpty(value['segment']) && { segment_type: segment_type }),
      ...(value?.['capacity'] &&
        !isEmpty(value['capacity']) && { capacity: value['capacity'] }),
      ...(value?.['transmission'] &&
        !isEmpty(value['transmission']) && {
          transmission: value['transmission'],
        }),
      ...(value?.['fuel_type'] &&
        !isEmpty(value['fuel_type']) && { fuel_type: value['fuel_type'] }),
      ...(value?.['seat'] &&
        value['seat'].length > 0 && { seat: value['seat'] }),
      ...(value?.['page'] && { page: value['page'] }),
      ...(value?.['keyword'] && { keyword: value['keyword'] }),
    };
  };

  const onChangeFilter = (value: any) => {
    let filterValueQuery;
    if (isArray(value) && value.length === 0) {
      filterValueQuery = {
        ...omit(filterValue, popperItem?.key),
      };
    } else {
      filterValueQuery = {
        ...filterValue,
        [popperItem?.key]: value,
      };
    }

    onFilter(filterValueQuery);
    onHandleClose();
  };

  const onClearKeyword = () => {
    setKeyword('');
    onFilter({ ...omit(filterValue, 'keyword') });
  };

  // ======================= BREADCRUMB PATH
  const selectedCarBrand = carBrand?.find(
    (item: any) => item?.car_brand_slug === router?.query?.slug
  );
  const breadcrumbOpt: BreadCrumbOptions[] = [
    {
      title: 'AutoFacts',
      url: !selectedCarBrand?.car_brand_name ? '' : basePath,
    },
    ...(selectedCarBrand?.car_brand_name
      ? [
          {
            title: `${selectedCarBrand?.car_brand_name || '-'}`,
            url: '',
          },
        ]
      : []),
  ];

  const isFiltered = Object.values(filterValue ?? {}).some(
    (value) => !isEmpty(value)
  );

  const totalFilter = Object.entries(filterValue ?? {})?.reduce(
    (acc, [key, value]: any) => {
      const keysIgnore = ['page', 'keyword'];
      // const rangesKey = ['monthly_payment', 'budget'];

      if (keysIgnore.includes(key)) return acc;
      // if (rangesKey.includes(key) && !isEmpty(value)) return (acc += 1);
      if (!isEmpty(value)) return (acc += 1);

      return acc;
    },
    0
  );

  const isFilterKeyword = filterValue
    ? 'keyword' in filterValue && filterValue.keyword !== ''
    : false;

  // =============== RENDERER VIEW
  const renderEVButton = () => {
    const filterFuelType = filterValue?.fuel_type;
    const selected =
      filterFuelType &&
      size(filterFuelType) === 1 &&
      filterFuelType[0] === 'ev';
    return (
      <QuickFilterButton
        borderRadius={'4px'}
        selected={selected}
        label={'EV Car'}
        onClick={() => {
          if (selected) {
            onFilter({ ...omit(filterValue, 'fuel_type') });
            return;
          }
          const bodyTypeFilter = {
            ...filterValue,
            fuel_type: ['ev'],
          };
          onFilter(bodyTypeFilter);
        }}
        {...(selected && {
          onDelete: (e) => {
            e.stopPropagation();
            const resetQuery = {
              ...filterValue,
              fuel_type: undefined,
            };
            onFilter(resetQuery);
          },
        })}
      />
    );
  };

  const renderFilter = () => {
    if (!settings) return null;
    return (
      <Stack
        sx={{
          display: 'inline-grid',
          zIndex: 10,
          position: { xs: 'sticky', lg: 'relative' },
          top: 0,
          padding: '10px 0',
          backgroundColor: 'white',
        }}
      >
        <Stack
          sx={{
            overflowX: 'auto',
            '::-webkit-scrollbar': {
              height: '3px',
            },
            '::-webkit-scrollbar-thumb': {
              backgroundColor: '#31ACBF',
              borderRadius: '50px',
            },
            '::-webkit-scrollbar-thumb:hover': {
              background: '#31ACBF',
            },
            paddingBottom: { xs: 1.5, lg: 0 },
            flexDirection: 'row',
            gap: 2,
          }}
        >
          {renderEVButton()}
          {/* Filter List */}
          {quickButtons.map((filterItem, index) => {
            if (!filterItem?.key) return;

            const renderOutput = () => {
              if (filterItem?.key !== 'body_type') {
                return filterValue?.[filterItem?.key]
                  ? `${filterItem?.label}: ${
                      filterValue?.[filterItem?.key]?.[0] > 0
                        ? 'RM ' +
                          formatter.format(
                            filterValue?.[filterItem?.key]?.[0]
                          ) +
                          ' - '
                        : ''
                    }RM ${formatter.format(
                      filterValue?.[filterItem?.key]?.[1]
                    )}`
                  : filterItem?.label;
              }
              const value = filterValue?.[filterItem?.key];
              const isArray = value && typeof value !== 'string';
              let label = filterItem?.label;
              if (isArray) {
                const filterLabel = value.map((text: string) =>
                  text.replace('-', ' ')
                );
                label = `${filterItem?.label}: ${filterLabel?.join(', ')}`;
              }

              return label;
            };
            const hasValue =
              filterValue?.[filterItem?.key] &&
              filterValue?.[filterItem?.key].length > 0;

            return (
              <QuickFilterButton
                key={`filter-${index}`}
                borderRadius={'4px'}
                selected={hasValue}
                label={hasValue ? renderOutput() : filterItem?.label}
                onClick={(event: React.MouseEvent<HTMLElement>) =>
                  onHandleClick(filterItem, event)
                }
                {...(hasValue && {
                  onDelete: (e) => {
                    e.stopPropagation();
                    const resetQuery = {
                      ...filterValue,
                      [filterItem.key]: undefined,
                    };
                    onFilter(resetQuery);
                  },
                })}
              />
            );
          })}

          {/* More Filter Action */}
          <QuickFilterButton
            borderRadius={'4px'}
            selected={isFiltered && totalFilter > 0}
            label={'More Filter'}
            icon={
              <Box
                height={'100%'}
                display={'flex'}
                alignItems={'center'}
                paddingLeft={'7px'}
                paddingRight={'4px'}
              >
                <FilterIcon
                  style={{ fill: isFiltered ? '#1E242F' : '#374255' }}
                />
              </Box>
            }
            onClick={() => setDialogOpen(true)}
            {...(isFiltered &&
              totalFilter > 0 && {
                onDelete: () => null,
                deleteIcon: (
                  <Box paddingRight={'4px'}>
                    <Avatar
                      sx={{
                        bgcolor: '#FB5858',
                        color: 'white',
                        width: 20,
                        height: 20,
                      }}
                    >
                      <Typography
                        fontSize={12}
                        textAlign={'center'}
                        color="white"
                        mt={0.2}
                      >
                        {totalFilter || 0}
                      </Typography>
                    </Avatar>
                  </Box>
                ),
              })}
          />

          {/* Clear Filter */}
          {!isEmpty(filterValue) && totalFilter > 0 && (
            <Button
              sx={{
                p: '4px 12px',
                whiteSpace: 'nowrap',
              }}
              variant="text"
              color="secondary"
              disableRipple
              onClick={onReset}
            >
              Clear All
            </Button>
          )}
          <Popover
            key={popperItem?.key}
            anchorEl={anchorEl}
            open={!!popperItem}
            onClose={onHandleClose}
          >
            <Stack sx={{ maxWidth: { xs: '100%', md: 458 }, gap: '10px' }}>
              <Typography fontWeight="500" mt={5} ml={5}>
                {popperItem?.label}
              </Typography>
              {popperItem?.render?.(
                filterValue?.[popperItem?.key],
                onChangeFilter
              )}
            </Stack>
          </Popover>
        </Stack>
      </Stack>
    );
  };

  const renderList = () => {
    const currentUrl = generateQueryToUrl(omit(query, 'page'), '/cars');

    if (filterData)
      return (
        <CarListingSection
          webView={webView}
          cars={filterData ?? []}
          title={'Filter Results'}
          pagination={{
            currentPage: filterPagination?.current_page,
            totalCount: filterPagination?.total,
            pageSize: filterPagination?.per_page,
            href: currentUrl,
            hasQuery: true,
          }}
        />
      );
    return (
      <div>
        <CarDescriptionSection
          data={selectedCarBrand}
          title={selectedCarBrand?.car_brand_name || ''}
          webView={webView}
          onFilterBrand={onFilterBrand}
        />

        {data?.newCarEV?.length > 0 && (
          <SectionTitle title="All EV">
            <CarListingSection webView={webView} cars={data?.newCarEV ?? []} />
          </SectionTitle>
        )}

        <SectionTitle
          title={data?.newCarLaunch ? 'Popular Car Models' : 'All Models'}
        >
          <CarListingSection
            webView={webView}
            cars={data?.newCarModels ?? []}
          />
        </SectionTitle>

        {data.newCarLaunch && data.newCarLaunch.length > 0 && (
          <SectionTitle title={'Recently Launched Car'}>
            <CarListingSection
              webView={webView}
              cars={data?.newCarLaunch ?? []}
            />
          </SectionTitle>
        )}
      </div>
    );
  };

  const renderMobileFilter = () => {
    if (isFilterKeyword) return null;
    return (
      <>
        {/* QUICK ACTION */}
        <QuickActionButtonList actions={getQuickActionButtons(webView)} />

        <Box
          sx={{
            display: {
              xs: 'none',
              md: 'flex',
              lg: 'none',
            },
          }}
        >
          <Stack gap={5}>
            <NewCarBrandGroup
              brands={[
                ...tabletPopularBrands,
                {
                  car_brand_slug: 'view-more',
                  car_brand_id: -1,
                  car_brand_name: 'View More',
                },
              ]}
              webView={webView}
              basePath={basePath}
              isNewCar
              filterProps={{
                items: carBrand,
                valueKey: 'car_brand_slug',
                labelKey: 'car_brand_name',
                imageKey: 'car_brand_image',
                initialBrandSlug: router.query.slug as string,
                onItemSelect: onFilterBrand,
                itemRight: false,
              }}
            />

            {/* Quick Filter */}
            <Grid container direction="row" spacing={2}>
              {quickFilters.map((item, index) => {
                const currentFilter = checkDefault(filterValue);
                const selected = item.value.every(({ type, value }) => {
                  const currentValue = currentFilter?.[type];
                  const bodyValue = value as string[];
                  return (
                    currentValue === value ||
                    (type === 'body_type' && currentValue?.[0] === bodyValue[0])
                  );
                });

                return (
                  <Grid item xs={3} key={`quick_filter_${index}`}>
                    <QuickFilterButton
                      borderRadius="4px"
                      selected={selected}
                      label={item.title}
                      onClick={() => {
                        if (selected) {
                          onFilter({ ...omit(filterValue, item.key) });
                          return;
                        }
                        if (item.key === 'budget') {
                          const budgetQuery = {
                            ...filterValue,
                            budget: [item.value[0].value, item.value[1].value],
                          };
                          onFilter(budgetQuery);
                        } else {
                          const bodyTypeFilter = {
                            ...filterValue,
                            body_type: item.value[0].value,
                          };
                          onFilter(bodyTypeFilter);
                        }
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
        </Box>
        {/* MOBILE VIEW FILTER */}
        <Box
          sx={{
            display: {
              xs: 'flex',
              md: 'none',
            },
          }}
        >
          <Stack gap={5}>
            <NewCarBrandGroup
              brands={[
                ...mobilePopularBrands,
                {
                  car_brand_slug: 'view-more',
                  car_brand_id: -1,
                  car_brand_name: 'View More',
                },
              ]}
              webView={webView}
              basePath={basePath}
              isNewCar
              filterProps={{
                items: carBrand,
                valueKey: 'car_brand_slug',
                labelKey: 'car_brand_name',
                imageKey: 'car_brand_image',
                initialBrandSlug: router.query.slug as string,
                onItemSelect: onFilterBrand,
                itemRight: false,
              }}
            />

            {/* Quick Filter */}
            <Grid container direction="row" spacing={2}>
              {quickFilters.map((item, index) => {
                const currentFilter = checkDefault(filterValue);
                const selected = item.value.every(({ type, value }) => {
                  const currentValue = currentFilter?.[type] ?? 0;
                  const bodyValue = value as string[];
                  const checkMin =
                    type === 'min_budget' && currentValue === 0 && value === 0;
                  return (
                    currentValue === value ||
                    (type === 'body_type' &&
                      currentValue &&
                      currentValue.includes(bodyValue[0])) ||
                    checkMin
                  );
                });
                return (
                  <Grid item xs={3} key={`quick_filter_${index}`}>
                    <QuickFilterButton
                      borderRadius="4px"
                      selected={selected}
                      label={item.title}
                      onClick={() => {
                        if (selected) {
                          onFilter({ ...omit(filterValue, item.key) });
                          return;
                        }
                        if (item.key === 'budget') {
                          const budgetQuery = {
                            ...filterValue,
                            budget: [item.value[0].value, item.value[1].value],
                          };
                          onFilter(budgetQuery);
                        } else {
                          const bodyTypeFilter = {
                            ...filterValue,
                            body_type: item.value[0].value,
                          };
                          onFilter(bodyTypeFilter);
                        }
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
        </Box>
      </>
    );
  };

  const renderCarList = () => {
    return (
      <Stack sx={{ gap: 10, position: 'relative', flexDirection: 'row' }}>
        {/* Filter Dialog */}
        {settings && (
          <FilterSection
            open={dialogOpen}
            value={filterValue}
            settings={settings}
            onClose={() => setDialogOpen(false)}
            onSubmit={onFilter}
            onReset={onReset}
            filterDialogRef={filterDialogRef}
          />
        )}

        {/* Desktop Left Side Bar */}
        <Stack sx={{ display: { xs: 'none', lg: 'flex' } }}>
          <Stack
            sx={{
              maxHeight: '100%',
              position: 'sticky',
              top: !webView ? 60 : 0,
              gap: 5,
            }}
          >
            <Stack gap={5}>
              <AlphabetListWithLetters
                title={'Browse by Brand'}
                items={carBrand}
                valueKey={'car_brand_slug'}
                labelKey={'car_brand_name'}
                imageKey={'car_brand_image'}
                initialBrandSlug={router.query.slug as string}
                onItemSelect={onFilterBrand}
                webView={webView}
                basePath={basePath}
              />
            </Stack>
          </Stack>
        </Stack>

        <Stack
          sx={{
            maxWidth: '100%',
            flexGrow: 1,
          }}
        >
          {!selectedCarBrand && (
            <TitleDivider
              title={'AutoFacts'}
              titleVariant="h1"
              paddingBlock="0"
              webView={webView}
              rightContent={
                <Box sx={{ marginLeft: { md: 'auto' }, pt: { xs: 0, md: 3 } }}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="BYD Seagull"
                    value={keyword}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const bodyTypeFilter = {
                          ...filterValue,
                          keyword,
                        };
                        onFilter(bodyTypeFilter);
                      }
                    }}
                    onChange={(val) => setKeyword(val.target.value.toString())}
                    sx={{
                      borderRadius: 40,
                      backgroundColor: 'transparent',
                      '& .MuiOutlinedInput-root': {
                        fontWeight: 500,
                        padding: '3px 3px 3px 14px !important',
                        '.MuiOutlinedInput-input': {
                          padding: '0 !important',
                        },
                        '& fieldset': {
                          border: 'none !important',
                        },
                        '&:hover fieldset': {
                          border: 'none !important',
                        },
                        '&.Mui-focused fieldset': {
                          border: '1px solid #31ACBF !important',
                          borderRadius: 40,
                        },
                      },
                      marginBottom: {
                        xs: '8px',
                        md: 0,
                      },
                    }}
                    InputProps={{
                      sx: {
                        '& input::placeholder': {
                          fontWeight: 500,
                          color: '#A8B5C9 !important',
                        },
                      },
                      startAdornment: (
                        <Stack sx={{ alignItems: 'center', marginRight: 2 }}>
                          <SearchIcon />
                        </Stack>
                      ),
                      endAdornment: (
                        <Stack direction="row" gap={1}>
                          {isFilterKeyword && (
                            <IconButton onClick={onClearKeyword}>
                              <ClearIcon fontSize={'small'} />
                            </IconButton>
                          )}
                          <Button
                            onClick={() => {
                              const bodyTypeFilter = {
                                ...filterValue,
                                keyword,
                              };
                              onFilter(bodyTypeFilter);
                            }}
                            variant={'contained'}
                            sx={{
                              borderRadius: 59,
                              backgroundColor: '#30A6B9',
                              padding: '10px 26px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              lineHeight: '1rem',
                              boxShadow: 'none !important',
                            }}
                          >
                            Search
                          </Button>
                        </Stack>
                      ),
                      style: {
                        borderRadius: 40,
                        border: '1px solid #E9ECF3',
                      },
                    }}
                  />
                </Box>
              }
            >
              <Stack
                style={{
                  gap: 20,
                  marginBottom: 20,
                }}
              >
                {/* FILTER */}
                {renderFilter()}
                {renderMobileFilter()}
              </Stack>
            </TitleDivider>
          )}

          {/* CAR LISTING */}
          {renderList()}
        </Stack>
      </Stack>
    );
  };

  // =============== VIEWS
  return (
    <Stack sx={{ gap: 5 }}>
      {/* BREADCRUMB */}
      {!webView && (
        <Stack>
          <Breadcrumb options={breadcrumbOpt} bold />
        </Stack>
      )}

      {/* CONTENT */}
      <Stack sx={{ gap: 10 }}>
        {renderCarList()}
        {/* <ModelSection
          models={popularCar}
          title="Popular New Car"
          webView={webView}
          isNewCar
        /> */}

        <div
          style={{
            display: isMobile ? 'none' : 'block',
            width: '100%',
          }}
        >
          <BrandFilter
            title={'Popular Brands in Malaysia'}
            brands={desktopPopularBrands}
            webView={webView}
            backgroundColor="#fff"
          />
        </div>

        <RecommendCarList webView={webView} />
      </Stack>
    </Stack>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default NewCarScreen;
